import { createSlice } from "@reduxjs/toolkit";
import Cookies from "js-cookie";

const readStoredTncStatus = () => {
  const raw = localStorage.getItem("tncStatus");
  if (raw === "true") return true;
  if (raw === "false") return false;
  return null;
};

// ---------------------------------------------------------------------------
// Deliberate-logout flag.
//
// Sign-out handlers that use `window.location.href` (rather than react-router's
// navigate) reload the page, which destroys the Redux store — so an in-memory
// isLogout flag was gone by the time SigninPage read it. SigninPage then took
// the /oauth2/authorize branch instead of the /login one, the auth server's SSO
// cookie was still valid, and it silently re-issued a token and dropped the user
// back on the dashboard. Hence "sign out twice".
//
// sessionStorage survives the reload but not the tab, which is exactly the
// lifetime this intent needs. localStorage would be wrong: sign-out handlers
// call localStorage.clear(), which would wipe the flag we just set.
// ---------------------------------------------------------------------------
const LOGOUT_INTENT_KEY = "ah_logout_intent";

const readLogoutIntent = () => {
  try {
    return sessionStorage.getItem(LOGOUT_INTENT_KEY) === "1";
  } catch (_) {
    return false; // storage blocked — fall back to in-memory behaviour
  }
};

const writeLogoutIntent = (value) => {
  try {
    if (value) sessionStorage.setItem(LOGOUT_INTENT_KEY, "1");
    else sessionStorage.removeItem(LOGOUT_INTENT_KEY);
  } catch (_) {
    // storage blocked — nothing to persist
  }
};

const initialState = {
  currentUser: "" || localStorage.getItem("currentUser"),
  accessToken: null || localStorage.getItem("e_access_token"),
  tncStatus: readStoredTncStatus(),
  userId: null || localStorage.getItem("userId"),
  userType: null || localStorage.getItem("userType"),
  isLogout: readLogoutIntent(),
  tenantId: null || localStorage.getItem("tenantId"),
};

const signinSlice = createSlice({
  name: "signinSlice",
  initialState,
  reducers: {
    setUserState: (state, action) => {
      const userType = (action.payload.userType || "").toLowerCase();
      state.currentUser = action.payload.userName;
      state.userType = userType;
      state.userId = action.payload.userId;
      state.accessToken = action.payload.token;
      state.isLogout = false;
      writeLogoutIntent(false);
      state.tenantId = action.payload.tenantId;

      // Only overwrite tncStatus when the payload carries an explicit boolean.
      // If the JWT/backend omits it (undefined/null), keep whatever is already
      // in storage — otherwise a previously accepted T&C gets silently
      // downgraded to the literal string "undefined" and the user is re-prompted.
      if (typeof action.payload.tncStatus === "boolean") {
        state.tncStatus = action.payload.tncStatus;
        localStorage.setItem("tncStatus", String(action.payload.tncStatus));
      } else {
        state.tncStatus = readStoredTncStatus();
      }

      // Update localStorage with all user data
      localStorage.setItem("currentUser", action.payload.userName);
      localStorage.setItem("userId", action.payload.userId);
      localStorage.setItem("userType", userType);
      localStorage.setItem("e_access_token", action.payload.token);
      localStorage.setItem("tenantId", action.payload.tenantId);

      // Also set cookie for cross-tab persistence
      Cookies.set("e_access_token", action.payload.token, { expires: 1 });
    },
    setUserId: (state, action) => {
      state.userId = action.payload;
      localStorage.setItem("userId", action.payload);
    },
    setTenantId: (state, action) => {
      state.tenantId = action.payload;
      localStorage.setItem("tenantId", action.payload);
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
      localStorage.setItem("currentUser", action.payload);
    },
    setAccessToken: (state, action) => {
      state.accessToken = action.payload;
      localStorage.setItem("e_access_token", action.payload);
      Cookies.set("e_access_token", action.payload, { expires: 1 });
    },
    setTnCStatus: (state, action) => {
      state.userId = action.payload.userId;
      state.tncStatus = action.payload.tncStatus;
      localStorage.setItem("tncStatus", action.payload.tncStatus);
      localStorage.setItem("userId", action.payload.userId);

      // Ensure we have the latest token when updating TNC status
      const currentToken =
        localStorage.getItem("e_access_token") || Cookies.get("e_access_token");
      if (currentToken) {
        state.accessToken = currentToken;
      }
    },
    setLogout: (state, action) => {
      // Clear all stored data
      Cookies.remove("e_access_token");
      localStorage.clear();

      // Reset state
      state.userType = "";
      state.accessToken = null;
      state.currentUser = "";
      state.tncStatus = null;
      state.userId = null;
      state.isLogout = true;
      state.tenantId = null;

      // Outlive the page reload that most sign-out handlers trigger, so
      // SigninPage knows this was a deliberate logout and must not silently
      // re-authenticate against the still-live SSO session.
      writeLogoutIntent(true);
    },
    resetLogoutFlag: (state, action) => {
      state.isLogout = false;
      writeLogoutIntent(false);
    },
    // New action to sync state with localStorage
    syncStateWithStorage: (state) => {
      const storedToken =
        localStorage.getItem("e_access_token") || Cookies.get("e_access_token");
      const storedUserId = localStorage.getItem("userId");
      const storedTncStatus = localStorage.getItem("tncStatus");
      const storedUserType = localStorage.getItem("userType");
      const storedCurrentUser = localStorage.getItem("currentUser");
      const storeTenantId = localStorage.getItem("tenantId");

      if (storedToken) state.accessToken = storedToken;
      if (storedUserId) state.userId = storedUserId;
      if (storedTncStatus !== null)
        state.tncStatus = storedTncStatus === "true";
      if (storedUserType) state.userType = storedUserType;
      if (storedCurrentUser) state.currentUser = storedCurrentUser;
      if (storeTenantId) state.tenantId = storeTenantId;
    },
  },
});

export const {
  setCurrentUser,
  setAccessToken,
  setTnCStatus,
  setLogout,
  setUserState,
  resetLogoutFlag,
  setUserId,
  syncStateWithStorage,
} = signinSlice.actions;

const signinSliceReducer = signinSlice.reducer;
export default signinSliceReducer;
